import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  REACT_APP_SOCIAL_GITHUB,
  REACT_APP_SOCIAL_LINKEDIN,
  REACT_APP_SOCIAL_TWITTER,
} from '../env';

const NAV = [
  { to: '/posts', label: 'Writing' },
  { to: '/about', label: 'About' },
];

/**
 * The wordmark is a USSD short code — the format Dennis's users dial to reach
 * the services he builds.
 */
export function Wordmark({ className = '' }) {
  return (
    <span className={`font-mono tracking-tight ${className}`}>
      <span className="text-signal">*</span>
      <span>dennis</span>
      <span className="text-signal">#</span>
    </span>
  );
}

export function SiteHeader() {
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-background/85 backdrop-blur-md transition-[border-color,box-shadow] duration-300 ${
        scrolled ? 'border-b border-rule' : 'border-b border-transparent'
      }`}
    >
      <div className="shell flex h-16 items-center justify-between gap-4">
        <Link to="/" className="text-base font-medium" aria-label="Home">
          <Wordmark />
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `label !text-[0.7rem] px-2.5 py-2 transition-colors ${
                  isActive ? 'text-signal' : 'text-muted-foreground hover:text-foreground'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            className="ml-1 grid h-9 w-9 place-items-center rounded-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <span className="relative block h-[18px] w-[18px]">
              <Sun
                className={`absolute inset-0 h-[18px] w-[18px] transition-all duration-500 ${
                  theme === 'dark' ? 'rotate-0 opacity-100 scale-100' : '-rotate-90 opacity-0 scale-50'
                }`}
              />
              <Moon
                className={`absolute inset-0 h-[18px] w-[18px] transition-all duration-500 ${
                  theme === 'dark' ? 'rotate-90 opacity-0 scale-50' : 'rotate-0 opacity-100 scale-100'
                }`}
              />
            </span>
          </button>
        </nav>
      </div>
    </header>
  );
}

const SOCIALS = [
  { href: REACT_APP_SOCIAL_GITHUB, label: 'GitHub' },
  { href: REACT_APP_SOCIAL_LINKEDIN, label: 'LinkedIn' },
  { href: REACT_APP_SOCIAL_TWITTER, label: 'Twitter' },
].filter((item) => Boolean(item.href));

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-rule">
      <div className="shell flex flex-col gap-8 py-12 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xs space-y-3">
          <Wordmark className="text-base" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            Software for the places connectivity forgot. Written and built in Kigali, Rwanda.
          </p>
        </div>

        <div className="flex gap-12">
          <nav className="space-y-2">
            <p className="label">Pages</p>
            <ul className="space-y-1.5 text-sm">
              <li><Link to="/" className="link-rule">Home</Link></li>
              {NAV.map((item) => (
                <li key={item.to}><Link to={item.to} className="link-rule">{item.label}</Link></li>
              ))}
            </ul>
          </nav>

          {SOCIALS.length > 0 && (
            <div className="space-y-2">
              <p className="label">Elsewhere</p>
              <ul className="space-y-1.5 text-sm">
                {SOCIALS.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className="link-rule">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="shell flex items-center justify-between border-t border-rule py-5 text-meta font-mono text-muted-foreground">
        <span>&copy; {new Date().getFullYear()} Dennis M. Nyabuti</span>
        <span className="text-signal">END</span>
      </div>
    </footer>
  );
}

/**
 * Resets scroll on navigation, and honours in-page anchors like /#work.
 */
export function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}
